import { cn } from "@/lib/utils";

/*
 * Thin race-coloured horizontal bar — the safe trim primitive.
 *
 * Use this anywhere we previously reached for `CheckeredStripe` at small
 * heights (header trim, page-section thresholds, hero outer trim). The
 * checker pattern needs ≥16 px of vertical room to render as a proper
 * 2-row checker; at 4–8 px it degrades into a row of tiny slivers that
 * read as a dotted-line bug. `RaceColorBar` carries the race-colour
 * identity (red + navy) without any pattern, so it stays clean at any
 * height.
 *
 * Variants:
 *   - "gradient" (default): red → navy → red symmetric blend. Reads as
 *                          a racing accent line; the default for trim
 *                          under the sticky header and page thresholds.
 *   - "solid":              flat primary (race red). Used when the bar
 *                          sits directly under a finish-line moment
 *                          (e.g. bottom of the hero artwork) and the
 *                          gradient would compete with the band above.
 *
 * For the genuine finish-line motif (≥16 px tall, with a real checker
 * pattern), reach for `CheckeredStripe` instead.
 */
export type RaceColorBarVariant = "gradient" | "solid";

export type RaceColorBarProps = {
  className?: string;
  /** Visual height in px. Defaults to 4 — thin trim. */
  height?: number;
  /** Colour treatment. Defaults to "gradient". */
  variant?: RaceColorBarVariant;
};

export function RaceColorBar({
  className,
  height = 4,
  variant = "gradient"
}: RaceColorBarProps) {
  const toneClass =
    variant === "gradient"
      ? "bg-gradient-to-r from-primary via-secondary to-primary"
      : "bg-primary";

  return (
    <div
      aria-hidden
      className={cn("w-full", toneClass, className)}
      style={{ height }}
    />
  );
}
